import { Component } from 'react';

import { lazyInject } from '../../context/module';
import { LoginService } from '../../services/login.service';
import { UIContext } from '../../services/ui-context';
import { PayPalComponent } from './payments/paypal.component';

interface PlanChangeComponentProps {
    /** The PayPal plan id (`P-...`) the user is about to subscribe to. */
    targetPlanId: string;

    /** Human-readable name for the target plan (shown in modal copy). */
    targetPlanName: string;

    /** Called with the new PayPal subscription id after approval. */
    completed: (subscriptionId?: string) => void;
}

interface PlanChangeComponentState {
    /**
     * Once the user has confirmed (or there was nothing to confirm in the
     * first place), we render the actual `<PayPalComponent>`. Switching this
     * flag is a one-way transition for the life of the component — there's
     * no "un-confirm" affordance.
     */
    confirmed: boolean;
}

/**
 * §4.2 F9 — Plan-supersede confirmation for /portal.
 *
 * When a logged-in user with an existing active PayPal subscription tries to
 * buy a *different* plan, we show a confirmation modal once, up front. The
 * modal explicitly tells them the old sub will be cancelled after the new
 * one is confirmed. On confirm, we swap to the actual PayPal/Card buttons.
 *
 * If the user has no active sub, or already has the same plan, we render
 * the PayPal buttons immediately — no extra click. This keeps the happy-path
 * checkout UX identical to /pricing.
 *
 * Why a wrapper component instead of intercepting the PayPal button click:
 * PayPal's button onClick is opaque (the button opens a popup immediately).
 * A native click-through is simpler than trying to reach into PayPal's DOM.
 *
 * Supersede cancellation of the OLD subscription happens server-side in
 * PaymentController.payPayPal after onApprove fires (§4.5). The modal copy
 * is what sets user expectations so they don't get a surprise cancellation
 * email; the backend then does the actual work.
 */
export class PlanChangeComponent extends Component<PlanChangeComponentProps, PlanChangeComponentState> {
    @lazyInject(LoginService) private loginService: LoginService;
    @lazyInject(UIContext) private uiContext: UIContext;

    readonly state: Readonly<PlanChangeComponentState> = {
        confirmed: !this.supersedeRequired(),
    };

    /**
     * True iff the logged-in user has an active PayPal sub on a DIFFERENT
     * plan than `targetPlanId`. Anonymous users and users without a PayPal
     * sub go straight to the PayPal buttons.
     *
     * Looks at `loginService.userDetails.paymentDetails.planId` if present.
     * Falls back to "no supersede needed" when the field is missing — the
     * backend still runs the same check (§4.5 monotonic filter) and handles
     * the supersede correctly either way, so a false negative here is safe.
     */
    private supersedeRequired(): boolean {
        const details = this.loginService.userDetails;
        if (!details?.isPaidUser()) return false;
        const existingPlanId: string | undefined = (details as any)?.paymentDetails?.planId
            || (details as any)?.paypalPlanId;
        if (!existingPlanId) return false;
        return existingPlanId !== this.props.targetPlanId;
    }

    private onChangePlanClick = () => {
        this.uiContext.showConfirmationWarningDialog(
            'Change Plan',
            `You currently have an active subscription. Subscribing to ${this.props.targetPlanName} will cancel your current subscription after your new subscription is confirmed. Continue?`,
            () => this.setState({ confirmed: true }),
        );
    };

    render() {
        if (this.state.confirmed) {
            return <PayPalComponent
                planId={this.props.targetPlanId}
                userId={this.loginService.userDetails?.id}
                completed={this.props.completed} />;
        }
        return <div>
            <button
                type="button"
                className="btn btn-primary w-100"
                onClick={this.onChangePlanClick}>
                Change to {this.props.targetPlanName}
            </button>
        </div>;
    }
}
